import React from 'react';
import { Tooltip } from 'antd';
import { CloudOutlined } from '@ant-design/icons';
import { isEmpty } from 'lodash';

import { formatWholeNumber } from '../../../../../../../../core/utils/campaigns';
import DataTable from '../../../../../../../shared/designSystem/components/DataTable';

// =============================================================================
// TransactionPublisherTable — v2 DataTable migration.
//
// V2 publisher data: { name, media (string | object), impressions,
// transactions, color }. `media` shape mirrors Creatives — URL string OR
// `{ key }` object. No expandable rows in v1 for this table (the expanded
// card file exists but was never wired in the v2 V2 path); preserving
// that behavior.
// =============================================================================

const numericSort = (key) => (rowA, rowB) =>
  (Number(rowA.original[key]) || 0) - (Number(rowB.original[key]) || 0);

const nameSort = (rowA, rowB) => {
  const a = rowA.original.name || '';
  const b = rowB.original.name || '';
  return String(a).localeCompare(String(b));
};

const hasMedia = (record) => {
  if (!record.media || record.media === '') return false;
  if (typeof record.media === 'string' && record.media.trim() !== '') return true;
  if (typeof record.media === 'object' && record.media.key) return true;
  return false;
};

const renderPublisherName = (text) => {
  if (!text) return <div />;
  if (text.length > 45) {
    return (
      <Tooltip overlayClassName="mjl-ds" title={text}>
        <span className="campaign-overview__creatives-name">
          {text.slice(0, 46)}…
        </span>
      </Tooltip>
    );
  }
  return <span className="campaign-overview__creatives-name">{text}</span>;
};

const TransactionPublisherTable = ({ publisherData }) => {
  const rows = publisherData || [];
  const hasThumbnailColumn = !isEmpty(rows) && rows.some((p) => hasMedia(p));

  const columns = [];

  columns.push({
    id: 'color',
    accessorKey: 'color',
    header: '',
    sortable: false,
    meta: { align: 'center', width: 24 },
    cell: ({ getValue }) => (
      <span
        className="campaign-overview__creatives-dot"
        style={{ backgroundColor: getValue() }}
        aria-hidden="true"
      />
    ),
  });

  if (hasThumbnailColumn) {
    columns.push({
      id: 'media',
      accessorKey: 'media',
      header: '',
      sortable: false,
      meta: { align: 'left', width: 60 },
      cell: ({ getValue }) => {
        const media = getValue();
        const url = typeof media === 'string' ? media : null;
        const show = url && url !== '';
        return (
          <div
            className="campaign-overview__creatives-thumb"
            style={show ? { backgroundImage: `url("${url}")` } : undefined}
          >
            {!show && <CloudOutlined style={{ fontSize: 20, color: '#94a3b8' }} />}
          </div>
        );
      },
    });
  }

  columns.push({
    id: 'name',
    accessorKey: 'name',
    header: 'Name',
    sortable: true,
    sortingFn: nameSort,
    meta: { align: 'left', width: 280 },
    cell: ({ getValue }) => renderPublisherName(getValue()),
  });

  columns.push({
    id: 'impressions',
    accessorKey: 'impressions',
    header: 'Impressions',
    sortable: true,
    sortingFn: numericSort('impressions'),
    meta: { align: 'right', width: 130 },
    cell: ({ getValue }) => {
      const v = getValue();
      return <span>{v ? formatWholeNumber(v) : 0}</span>;
    },
  });
  columns.push({
    id: 'transactions',
    accessorKey: 'transactions',
    header: 'Transactions',
    sortable: true,
    sortingFn: numericSort('transactions'),
    meta: { align: 'right', width: 130 },
    cell: ({ getValue }) => {
      const v = getValue();
      return <span>{v ? formatWholeNumber(v) : 0}</span>;
    },
  });

  const totalImpressions = rows.reduce(
    (sum, d) => sum + (parseFloat(d.impressions) || 0),
    0
  );
  const totalTransactions = rows.reduce(
    (sum, d) => sum + (parseFloat(d.transactions) || 0),
    0
  );

  const renderMobileCard = (record) => {
    const media = record.media;
    const url = typeof media === 'string' ? media : null;
    const show = url && url !== '';
    return (
      <>
        <div className="campaign-overview__creatives-card-head">
          <div
            className="campaign-overview__creatives-thumb"
            style={show ? { backgroundImage: `url("${url}")` } : undefined}
          >
            {!show && <CloudOutlined style={{ fontSize: 20, color: '#94a3b8' }} />}
          </div>
          <div className="campaign-overview__creatives-card-name">
            <span
              className="campaign-overview__creatives-dot"
              style={{ backgroundColor: record.color }}
              aria-hidden="true"
            />
            {renderPublisherName(record.name)}
          </div>
        </div>
        <div className="campaign-overview__creatives-card-metrics">
          <div className="campaign-overview__creatives-card-metric">
            <span className="campaign-overview__creatives-card-metric-label">Impressions</span>
            <span className="campaign-overview__creatives-card-metric-value">
              {record.impressions ? formatWholeNumber(record.impressions) : '-'}
            </span>
          </div>
          <div className="campaign-overview__creatives-card-metric">
            <span className="campaign-overview__creatives-card-metric-label">Transactions</span>
            <span className="campaign-overview__creatives-card-metric-value">
              {record.transactions ? formatWholeNumber(record.transactions) : '-'}
            </span>
          </div>
        </div>
      </>
    );
  };

  return (
    <DataTable
      data={rows}
      columns={columns}
      pageSize={10}
      mobileBreakpoint="md"
      renderMobileCard={renderMobileCard}
      emptyState="No publishers yet."
    />
  );
};

export default TransactionPublisherTable;
